import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import QualificationForm from './QualificationForm';
import QualificationHistory from './QualificationHistory';
import MoscowSegmentedBar from './MoscowSegmentedBar';
import ScoreProgressBar from '../../Common/ScoreProgressBar';
import { Qualification, QualificationCreate, QualificationBase } from '../../../types';
import { Button } from '../../Common/button';

interface UseCaseQualificationTabProps {
  useCaseId: string;
  latestQualification: Qualification | null;
  qualificationHistory: Qualification[];
  createQualification: (data: QualificationCreate) => Promise<Qualification | null>;
  updateQualification: (qualificationId: string, data: QualificationBase) => Promise<Qualification | null>;
  deleteQualification: (qualificationId: string) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
  editingQualification: Qualification | null;
  setEditingQualification: React.Dispatch<React.SetStateAction<Qualification | null>>;
  /** ID of the currently authenticated user */
  currentUserId?: string;
  /** Whether the current user is an org admin or super-admin */
  isAdmin?: boolean;
}

const UseCaseQualificationTab: React.FC<UseCaseQualificationTabProps> = ({
  useCaseId,
  latestQualification,
  qualificationHistory,
  createQualification,
  updateQualification,
  deleteQualification,
  isLoading,
  error,
  editingQualification,
  setEditingQualification,
  currentUserId,
  isAdmin = false,
}) => {
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState(false);

  const showForm = editMode || editingQualification != null;

  const handleEditClick = (qualification: Qualification) => {
    setEditingQualification(qualification);
    setEditMode(false);
    document.getElementById('qualification-form-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleFormSubmit = async (data: QualificationCreate | QualificationBase) => {
    if (editingQualification) {
      await updateQualification(editingQualification.id, data as QualificationBase);
      setEditingQualification(null);
    } else {
      await createQualification(data as QualificationCreate);
      setEditMode(false);
    }
  };

  const handleCancelNew = () => {
    setEditMode(false);
  };

  const handleCancelEdit = () => {
    setEditingQualification(null);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-6">
      <div className="flex items-center justify-between border-b border-border pb-2">
        <h2 className="text-xl font-semibold text-foreground">{t('useCaseQualification.title')}</h2>
        {!showForm && !isLoading && (
          <Button type="button" size="sm" onClick={() => setEditMode(true)}>
            {t('useCaseQualification.newQualification')}
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-400 text-red-600 px-4 py-3 rounded-md" role="alert">
          <strong className="font-semibold">{t('useCaseQualification.errorTitle')}</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      )}

      {isLoading && <p className="text-center text-muted-foreground">{t('useCaseQualification.loading')}</p>}

      {!isLoading && !showForm && (
        <>
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground">{t('useCaseQualification.activeQualification')}</h3>
            {latestQualification ? (
              <div className="p-4 border border-border rounded-lg bg-muted/30 space-y-4 text-sm">
                {latestQualification.needs_review && (
                  <div className="flex flex-col gap-1 p-3 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-800 dark:text-amber-200 dark:border-amber-400/40 dark:bg-amber-500/10" role="alert">
                    <p className="text-sm font-medium">{t('useCaseQualification.reviewRequiredWarning')}</p>
                    {latestQualification.review_type && (
                      <p className="text-xs">
                        {t('useCaseQualification.reviewRequiredWarningType', {
                          type: latestQualification.review_type === 'Other' && latestQualification.review_other_name
                            ? latestQualification.review_other_name
                            : t(`qualification.form.reviewType${latestQualification.review_type === 'Legal' ? 'Legal' : latestQualification.review_type === 'Tech' ? 'Tech' : 'Other'}`),
                        })}
                      </p>
                    )}
                  </div>
                )}
                <ScoreProgressBar
                  label={t('qualification.form.valueScoreLabel')}
                  value={latestQualification.value_score ?? null}
                  max={10}
                  barColor="bg-green-600"
                />
                <ScoreProgressBar
                  label={t('qualification.form.complexityScoreLabel')}
                  value={latestQualification.complexity_score ?? null}
                  max={10}
                  barColor="bg-amber-500"
                />
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-foreground">{t('qualification.form.moscowLabel')}</span>
                  </div>
                  <MoscowSegmentedBar value={latestQualification.moscow ?? null} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ScoreProgressBar
                    label={t('qualification.form.riceReachLabel')}
                    value={latestQualification.rice_reach ?? null}
                    max={100}
                    barColor="bg-blue-500"
                  />
                  <ScoreProgressBar
                    label={t('qualification.form.riceImpactLabel')}
                    value={latestQualification.rice_impact ?? null}
                    max={3}
                    barColor="bg-indigo-500"
                  />
                  <ScoreProgressBar
                    label={t('qualification.form.riceConfidenceLabel')}
                    value={latestQualification.rice_confidence ?? null}
                    max={100}
                    valueLabel={latestQualification.rice_confidence != null ? `${latestQualification.rice_confidence}%` : undefined}
                    barColor="bg-violet-500"
                  />
                  <ScoreProgressBar
                    label={t('qualification.form.riceEffortLabel')}
                    value={latestQualification.rice_effort ?? null}
                    max={50}
                    barColor="bg-slate-500"
                  />
                </div>
                <div className="text-xs text-muted-foreground pt-2 border-t border-border space-y-0.5">
                  <p><strong>R:</strong> {t('qualification.rice.reachDesc')}</p>
                  <p><strong>I:</strong> {t('qualification.rice.impactDesc')}</p>
                  <p><strong>C:</strong> {t('qualification.rice.confidenceDesc')}</p>
                  <p><strong>E:</strong> {t('qualification.rice.effortDesc')}</p>
                </div>
                {latestQualification.notes && (
                  <div>
                    <span className="text-muted-foreground">{t('global.notes')}: </span>
                    <span className="text-foreground">{latestQualification.notes}</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">{t('useCaseQualification.noQualificationYet')}</p>
            )}
          </div>
        </>
      )}

      <div id="qualification-form-section">
        {!isLoading && showForm && (
          <QualificationForm
            useCaseId={useCaseId}
            initialData={editingQualification ?? null}
            onSubmit={handleFormSubmit}
            isEditing={!!editingQualification}
            isLoading={isLoading}
            onCancelEdit={editingQualification ? handleCancelEdit : handleCancelNew}
            submitLabel={editMode && !editingQualification ? t('useCaseQualification.saveNewQualification') : undefined}
          />
        )}
      </div>

      {!isLoading && (
        <QualificationHistory
          history={qualificationHistory}
          onDelete={deleteQualification}
          onEdit={handleEditClick}
          isLoading={isLoading}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
};

export default UseCaseQualificationTab;
